import { faDownload } from "@fortawesome/pro-regular-svg-icons";

import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import { TablePagination } from "../TablePagination/TablePagination";
import { TableFooterProps } from "./table-footer.types";

type TableFooterPaginationBarProps = Pick<
	TableFooterProps,
	| "columns"
	| "coulumsShown"
	| "currentPage"
	| "currentlyDownloadingSelected"
	| "exportIsLoading"
	| "handleChangeRowsPerPage"
	| "handleExport"
	| "handleNavigateToPage"
	| "hideColumnsCount"
	| "hidePagination"
	| "limit"
	| "overallCount"
	| "rowsPerPage"
	| "totalPages"
>;

/**
 * The always-on strip under a table: export, row and column counts, and pagination. Extracted from
 * `TableFooter` (over the 500-line ceiling) when the selection bar gained a generic action slot
 * (PREC-3240).
 */
export const TableFooterPaginationBar: FunctionComponent<TableFooterPaginationBarProps> = ({
	columns,
	coulumsShown,
	currentPage,
	currentlyDownloadingSelected,
	exportIsLoading,
	handleChangeRowsPerPage,
	handleExport,
	handleNavigateToPage,
	hideColumnsCount,
	hidePagination,
	limit,
	overallCount,
	rowsPerPage,
	totalPages,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`basics.${key}`);

	return (
		<div
			className="fixed flex flex-row items-center w-auto h-[38px] border-t border-t-border bottom-0 right-0 z-150 bg-white overflow-hidden"
			id="table-footer"
		>
			<div className="absolute inset-0 flex flex-row items-center px-5 gap-3 text-xs text-text-meta">
				{handleExport && (
					<>
						<button
							className="flex items-center gap-1 text-text-meta hover:text-blue-action font-medium cursor-pointer"
							disabled={currentlyDownloadingSelected || exportIsLoading}
							type="button"
							onClick={() => handleExport()}
						>
							<Icon className="w-3.5 h-3.5" icon={faDownload} />
							{ts("export")}
						</button>
						<span className="w-px h-4 bg-border-light" />
					</>
				)}
				{rowsPerPage || overallCount ? (
					<>
						<span className="whitespace-nowrap">
							{ts("rows")}:{" "}
							<span className="text-text">
								{rowsPerPage || ""}
								{overallCount ? <>/{overallCount >= 10000 ? "10000+" : overallCount}</> : ""}
							</span>
						</span>
						{!hideColumnsCount && <span className="w-px h-4 bg-border-light" />}
					</>
				) : null}
				{!hideColumnsCount && (
					<span className="whitespace-nowrap">
						{ts("columnsShown")}:{" "}
						<span className="text-text">
							{coulumsShown}/{columns}
						</span>
					</span>
				)}
				<div className="flex-1" />
				{!hidePagination && handleNavigateToPage && (
					<TablePagination
						currentPage={currentPage ?? 1}
						handleChangeRowsPerPage={handleChangeRowsPerPage}
						handleNavigateToPage={handleNavigateToPage}
						limit={limit ?? 15}
						maxPage={totalPages ?? 1}
					/>
				)}
			</div>
		</div>
	);
};
